import './daily.css';
import { t } from './i18n';
import type { VariantId } from '../config';
import type { DailyEntry, DailyRun } from '../net/protocol';

/**
 * 이번주의 매치 — the weekly solo challenge panel, and our lead hype feature.
 *
 * One layout a week (resets Monday), identical for everyone, a handful of
 * attempts, ranked by how few strokes it took you to run the target — plus a
 * coin award for the week's top 5. It's the answer to "I opened the app and
 * nobody's online": the ladder needs an opponent, this doesn't.
 */
export interface DailyState {
  /** Week key (the Monday's KST date) — resets every Monday 00:00 KST. */
  day: string;
  /** Epoch-ms when this week's layout is replaced. */
  endsAt: number;
  variant: VariantId;
  target: number;
  maxAttempts: number;
  maxStrokes: number;
  /** The day's shared ball layout (the App places these on the table). */
  positions: Record<string, { x: number; y: number }>;
  /** 'banks' demands at least `minBanks` of the target caroms be bank shots.
   *  'timedScore' = Time Attack (most caroms in a time limit); 'timedSpeed' = Time Attack (fastest to clear the target). */
  ruleType: 'caroms' | 'banks' | 'timedScore' | 'timedSpeed';
  minBanks: number;
  /** Starting cue-ball aim angle override, radians. Null = auto-face the nearest object ball. */
  cueAngle: number | null;
  /** Optional admin-set display name for this week's mission. */
  title: string | null;
  /** 'timedScore' only: the countdown length, seconds. */
  timeLimitSec: number | null;
  mine: DailyRun | null;
  entries: DailyEntry[];
}

/** "12.3" from a millisecond duration — the shared time-display format. */
function secLabel(ms: number): string {
  return (ms / 1000).toFixed(1);
}

export interface DailyPanelCallbacks {
  onPlay(): void;
  onClose(): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

/** "2시간 10분 후 초기화" — how long until Monday's reset. */
export function rerollLabel(endsAt: number): string {
  const left = Math.max(0, endsAt - Date.now());
  const mins = Math.ceil(left / 60000);
  if (mins <= 1) return t('daily.rerollSoon');
  if (mins < 60) return t('daily.rerollMin', { n: mins });
  return t('daily.rerollHr', { h: Math.floor(mins / 60), m: mins % 60 });
}

export class DailyPanel {
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly sub = el('div', 'daily-sub');
  private readonly goal = el('div', 'daily-goal');
  private readonly mine = el('div', 'daily-mine');
  private readonly play = el('button', 'ui-btn ui-btn--go ui-btn--block');
  private readonly note = el('div', 'daily-note');
  private readonly board = el('div', 'daily-board');
  private state: DailyState | null = null;
  /** Set when the panel is showing the outcome of a just-finished attempt. */
  private flash = '';

  constructor(root: HTMLElement, private cb: DailyPanelCallbacks) {
    this.panel.id = 'daily-panel';
    this.panel.style.display = 'none';

    const header = el('div', 'review-header');
    const heads = el('div', 'review-heads');
    const title = el('div', 'review-title');
    title.textContent = t('daily.title');
    heads.append(title, this.sub);
    const close = el('button', 'review-close');
    close.textContent = t('mainmenu.back');
    close.onclick = () => {
      this.hide();
      this.cb.onClose();
    };
    header.append(heads, close);

    this.play.onclick = () => this.cb.onPlay();

    const boardLabel = el('div', 'ui-label daily-boardlabel');
    boardLabel.textContent = t('daily.board');

    this.panel.append(header, this.goal, this.mine, this.play, this.note, boardLabel, this.board);
    root.appendChild(this.panel);
  }

  get isOpen(): boolean {
    return this.panel.style.display !== 'none';
  }

  open(state: DailyState): void {
    this.setState(state);
    this.panel.style.display = 'flex';
  }

  hide(): void {
    this.panel.style.display = 'none';
    this.flash = '';
  }

  /** Show the outcome of the attempt that just ended, above the usual state. */
  showResult(state: DailyState, cleared: boolean, strokes: number, caroms: number, elapsedMs?: number): void {
    if (state.ruleType === 'timedScore') this.flash = t('daily.timeUp', { n: caroms });
    else if (state.ruleType === 'timedSpeed' && cleared && elapsedMs != null) this.flash = t('daily.clearedTime', { s: secLabel(elapsedMs) });
    else this.flash = cleared ? t('daily.cleared', { n: strokes }) : t('daily.failed', { n: caroms });
    this.open(state);
  }

  setState(state: DailyState): void {
    this.state = state;
    this.render();
  }

  private render(): void {
    const s = this.state;
    if (!s) return;
    const unlimitedAttempts = s.maxAttempts === 0;
    const attemptsLeft = unlimitedAttempts ? Infinity : Math.max(0, s.maxAttempts - (s.mine?.attempts ?? 0));

    // The layout rerolls on a timer, so lead with how long this one has left —
    // that's the thing that decides whether to play now or come back.
    this.sub.textContent = s.title
      ? `${s.title} · ${t(`variant.${s.variant}`)} · ${rerollLabel(s.endsAt)}`
      : `${t(`variant.${s.variant}`)} · ${rerollLabel(s.endsAt)}`;
    this.goal.textContent =
      s.ruleType === 'banks' ? t('daily.goalBanks', { t: s.target, b: s.minBanks })
      : s.ruleType === 'timedScore' ? t('daily.goalTimed', { s: s.timeLimitSec ?? 60 })
      : s.ruleType === 'timedSpeed' ? t('daily.goalSpeed', { t: s.target })
      : t('daily.goal', { t: s.target });

    // "where do I stand" — the flash line (just-finished attempt) wins if present
    this.mine.classList.toggle('is-flash', !!this.flash);
    if (this.flash) this.mine.textContent = this.flash;
    else if (s.ruleType === 'timedScore') this.mine.textContent = s.mine && s.mine.attempts > 0 ? t('daily.bestScore', { n: s.mine.caroms }) : t('daily.untried');
    else if (s.ruleType === 'timedSpeed' && s.mine?.elapsedMs != null) this.mine.textContent = t('daily.bestTime', { s: secLabel(s.mine.elapsedMs) });
    else if (s.mine?.strokes != null) this.mine.textContent = t('daily.best', { n: s.mine.strokes });
    else if (s.mine && s.mine.attempts > 0) this.mine.textContent = t('daily.noClear', { n: s.mine.caroms });
    else this.mine.textContent = t('daily.untried');

    if (attemptsLeft > 0) {
      this.play.disabled = false;
      this.play.textContent = s.mine?.attempts
        ? (unlimitedAttempts ? t('daily.retryUnlimited') : t('daily.retry', { n: attemptsLeft }))
        : t('daily.play');
    } else {
      this.play.disabled = true;
      this.play.textContent = t('daily.spent');
    }
    this.note.textContent = unlimitedAttempts ? t('daily.subUnlimited') : t('daily.sub', { n: s.maxAttempts });

    this.board.replaceChildren();
    if (!s.entries.length) {
      const empty = el('div', 'daily-empty');
      empty.textContent = t('daily.empty');
      this.board.appendChild(empty);
      return;
    }
    for (const e of s.entries) {
      const row = el('div', 'daily-row');
      const rank = el('span', 'daily-rank');
      rank.textContent = String(e.rank);
      const name = el('span', 'daily-name');
      name.textContent = e.name;
      const score = el('span', 'daily-score');
      if (s.ruleType === 'timedScore') {
        score.textContent = t('daily.caroms', { n: e.caroms });
      } else if (s.ruleType === 'timedSpeed') {
        if (e.elapsedMs != null) score.textContent = t('daily.time', { s: secLabel(e.elapsedMs) });
        else {
          score.textContent = t('daily.dnf');
          score.classList.add('is-dnf');
        }
      } else if (e.strokes != null) {
        score.textContent = t('daily.strokes', { n: e.strokes });
      } else {
        score.textContent = t('daily.dnf');
        score.classList.add('is-dnf');
      }
      row.append(rank, name, score);
      this.board.appendChild(row);
    }
  }
}
